import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { CreditCard, Landmark, Loader2, Receipt } from 'lucide-react';
import { Drawer } from '@components/Drawer';
import reportService from '@services/reportService';
import { REPORT_BASIS_LABELS, type ReportBasis, type ReportEntry } from '@/types/finance';
import { formatDateBR } from '@/utils/formatters';
import { formatBRL } from '@/utils/money';
import { monthLabel, monthRange } from '@/utils/period';
import { ColorDot, ProjectedBadge } from './financeBits';

export interface ReportEntriesTarget {
  year: number;
  month: number;
  /** 0 = "Sem categoria". */
  categoryId: number;
  /** Soma as filhas (categoria raiz). */
  includeChildren: boolean;
  basis: ReportBasis;
  /** Nome da categoria (título do painel). */
  title: string;
  color?: string | null;
}

interface Props {
  target: ReportEntriesTarget | null;
  onClose: () => void;
  onError: (err: unknown) => void;
}

const SourceIcon: React.FC<{ source: ReportEntry['source'] }> = ({ source }) =>
  source === 'card' ? (
    <span className="p-1.5 rounded-lg bg-violet-50 text-violet-600 flex-shrink-0" title="Item de fatura de cartão">
      <CreditCard size={14} />
    </span>
  ) : (
    <span className="p-1.5 rounded-lg bg-sky-50 text-sky-600 flex-shrink-0" title="Lançamento em conta">
      <Landmark size={14} />
    </span>
  );

/**
 * Drill-down dos relatórios e do orçamento (GET /reports/expenses/transactions): lançamentos de
 * conta e itens de cartão da categoria no mês, com origem (conta/cartão e fatura), data,
 * descrição, valor (positivo) e etiqueta "Previsto". Reutilizado por Orçamento e Despesas do mês.
 */
export const ReportEntriesDrawer: React.FC<Props> = ({ target, onClose, onError }) => {
  const [rows, setRows] = useState<ReportEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    setLoading(true);
    setRows([]);
    reportService
      .getExpenseEntries({ year: target.year, month: target.month, category_id: target.categoryId, basis: target.basis, include_children: target.includeChildren })
      .then((list) => !cancelled && setRows(list))
      .catch((err) => !cancelled && onError(err))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.year, target?.month, target?.categoryId, target?.includeChildren, target?.basis]);

  const total = rows.reduce((s, r) => s + r.amount_cents, 0);
  const range = target ? monthRange(target.year, target.month) : null;

  return (
    <Drawer
      isOpen={!!target}
      onClose={onClose}
      size="lg"
      title={
        target && (
          <span className="inline-flex items-center gap-2 min-w-0 max-w-full">
            <Receipt size={18} className="text-slate-500 flex-shrink-0" />
            <ColorDot color={target.color} />
            <span className="truncate">{target.title}</span>
            <span className="text-sm font-normal text-slate-500 whitespace-nowrap first-letter:uppercase">· {monthLabel({ year: target.year, month: target.month })}</span>
          </span>
        )
      }
      footer={
        target && (
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-slate-600">
              {rows.length} {rows.length === 1 ? 'lançamento' : 'lançamentos'} · {REPORT_BASIS_LABELS[target.basis].toLowerCase()}
              {target.includeChildren ? ' · com subcategorias' : ''}
            </span>
            <span className="font-semibold text-slate-800 tabular-nums">{formatBRL(total)}</span>
          </div>
        )
      }
    >
      {loading ? (
        <p className="text-center text-slate-400 py-10">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </p>
      ) : rows.length === 0 ? (
        <p className="text-center text-slate-500 py-10">Nenhum lançamento nesta categoria no mês.</p>
      ) : (
        <>
          <ul className="divide-y divide-slate-100 -mx-2">
            {rows.map((r) => (
              <li key={`${r.source}-${r.id}`} className="flex items-start gap-3 px-2 py-2.5">
                <SourceIcon source={r.source} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800 [overflow-wrap:anywhere]">{r.description}</p>
                  <p className="text-small flex flex-wrap items-center gap-x-1.5">
                    <span className="whitespace-nowrap">{formatDateBR(r.date)}</span>
                    <span>·</span>
                    <span className="truncate">{r.origin_name}</span>
                    {r.invoice_label && <span className="whitespace-nowrap">· fatura {r.invoice_label}</span>}
                    {target?.includeChildren && r.category_name && r.category_name !== target.title && <span className="whitespace-nowrap">· {r.category_name}</span>}
                    {r.is_projected && <ProjectedBadge />}
                  </p>
                </div>
                <span className={clsx('text-sm font-medium tabular-nums whitespace-nowrap text-slate-800', r.is_projected && 'italic')}>{formatBRL(r.amount_cents)}</span>
              </li>
            ))}
          </ul>
          {range && target && target.categoryId > 0 && (
            <p className="text-small mt-3">
              <Link to={`/finance/transactions?start=${range.start}&end=${range.end}&category_id=${target.categoryId}`} className="text-emerald-700 hover:underline">
                Ver os lançamentos de conta desta categoria em Lançamentos
              </Link>
            </p>
          )}
        </>
      )}
    </Drawer>
  );
};

export default ReportEntriesDrawer;
